import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useMemo } from 'react';

import axios, { AxiosResponse } from 'axios';

import { Comments } from '@/api/Comments';
import { CommentReadWatermarkResponse } from '@/api/data-contracts';
import { CommentWatermarkResult } from '@/shared/lib/commentWatermarkUtils';

const useCommentsService = () => {
  const { keycloak } = useKeycloak();

  const commentsApi = useMemo(
    () =>
      new Comments({
        headers: { Authorization: `Bearer ${keycloak.token}` },
      }),
    [keycloak.token],
  );

  const markCommentScopeRead = useCallback(
    async (
      scopeType: 'ORDER' | 'MEDIA_PLAN' | 'TASK' | 'ATTACHMENT',
      resourceId: string,
      subresourceId?: string,
    ): Promise<void> => {
      await keycloak.updateToken(30);
      const response = await commentsApi.markRead({
        scopeType,
        resourceId,
        subresourceId: subresourceId,
      });

      if (response.status < 200 || response.status >= 300) {
        throw new Error(
          `Marking ${scopeType} ${resourceId} read failed with status ${response.status}`,
        );
      }
    },
    [commentsApi, keycloak],
  );

  const getCommentWatermark = useCallback(
    async (
      scopeType: 'ORDER' | 'MEDIA_PLAN' | 'TASK' | 'ATTACHMENT',
      resourceId: string,
      subresourceId?: string,
      signal?: AbortSignal,
    ): Promise<CommentWatermarkResult> => {
      let response: AxiosResponse<CommentReadWatermarkResponse>;

      try {
        response = await commentsApi.getWatermark(
          { scopeType, resourceId, subresourceId },
          { signal },
        );
      } catch (error) {
        // Axios rejects every non-2xx, so a status only exists on error.response. Its absence
        // means the request never completed (offline or aborted), which is not a contract bug.
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        const requestLabel = `GET /comments/read (${scopeType} ${resourceId}${
          subresourceId ? `/${subresourceId}` : ''
        })`;
        if (status === 400) {
          console.error(
            `[comments] ${requestLabel} returned 400 — the request violates the read-watermark contract`,
          );
        } else if (status !== undefined && import.meta.env.DEV) {
          console.warn(`[comments] ${requestLabel} failed with status ${status}`);
        }
        return { lastReadAt: null, fromServer: false };
      }

      const raw = response.data?.lastReadAt as string | number | null | undefined;
      return {
        lastReadAt: typeof raw === 'number' ? new Date(raw).toISOString() : (raw ?? null),
        fromServer: true,
      };
    },
    [commentsApi],
  );

  return { markCommentScopeRead, getCommentWatermark };
};

export default useCommentsService;
